import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";
import { Lock, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { api } from "@/services/api";
import SEO from "@/components/SEO";

const BG_IMAGE = "https://images.unsplash.com/photo-1651342489644-be11c7ee318d";

export default function PasswordResetPage() {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    if (!tokenParam) {
      toast.error("Invalid reset link");
      nav("/forgot-password");
      return;
    }
    setToken(tokenParam);
    validateToken(tokenParam);
  }, [searchParams, nav]);

  const validateToken = async (token) => {
    try {
      await api.post("/password-reset/validate", { token });
      setTokenValid(true);
    } catch (e) {
      console.error(e);
      setTokenValid(false);
      toast.error("Invalid or expired reset link");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      return toast.error("Password must be at least 8 characters long");
    }
    
    if (password !== confirmPassword) {
      return toast.error("Passwords do not match");
    }
    
    setLoading(true);
    try {
      await api.post("/password-reset/reset", { token, new_password: password });
      setResetSuccess(true);
      toast.success("Password reset successfully!");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <>
        <SEO
          title="Reset Password"
          description="Reset your Bill Forge account password."
          path="/password-reset"
          noindex={true}
        />
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#0A0A0B]">
          <div className="text-slate-500">Validating reset link...</div>
        </div>
      </>
    );
  }

  if (tokenValid === false) {
    return (
      <>
        <SEO
          title="Reset Password"
          description="Reset your Bill Forge account password."
          path="/password-reset"
          noindex={true}
        />
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white dark:bg-[#0A0A0B] text-slate-900 dark:text-slate-100">
          <Toaster position="top-right" />
        <div className="hidden lg:block relative overflow-hidden">
          <img src={BG_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A0A0B]/60" />
        </div>
        <div className="flex items-center justify-center p-6 sm:p-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <button
              onClick={() => nav("/forgot-password")}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Request new link
            </button>
            <h1 className="text-4xl font-extrabold tracking-tighter mb-4">Invalid Link</h1>
            <p className="text-slate-500 mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
          </motion.div>
        </div>
      </div>
      </>
    );
  }

  if (resetSuccess) {
    return (
      <>
        <SEO
          title="Reset Password"
          description="Reset your Bill Forge account password."
          path="/password-reset"
          noindex={true}
        />
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white dark:bg-[#0A0A0B] text-slate-900 dark:text-slate-100">
          <Toaster position="top-right" />
        <div className="hidden lg:block relative overflow-hidden">
          <img src={BG_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A0A0B]/60" />
        </div>
        <div className="flex items-center justify-center p-6 sm:p-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <h1 className="text-4xl font-extrabold tracking-tighter mb-4">Password Reset!</h1>
            <p className="text-slate-500 mb-8">
              Your password has been successfully reset. You can now login with your new password.
            </p>
            <button
              onClick={() => nav("/login")}
              className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-4 rounded-sm font-semibold hover:bg-[#002FA7] hover:text-white transition-colors flex items-center justify-center gap-2"
            >
              Go to Login <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Reset Password"
        description="Reset your Bill Forge account password."
        path="/password-reset"
        noindex={true}
      />
      <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white dark:bg-[#0A0A0B] text-slate-900 dark:text-slate-100">
        <Toaster position="top-right" />
      {/* Side visual */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={BG_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0A0A0B]/60" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="text-2xl font-extrabold tracking-tighter">BILL FORGE</div>
          <div>
            <div className="uppercase text-xs tracking-[0.3em] opacity-70 mb-4">Invoicing / Precision</div>
            <div className="text-4xl font-light leading-tight max-w-sm">
              Ship invoices your customers actually respect.
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <button
            onClick={() => nav("/login")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>

          <div className="uppercase text-xs tracking-[0.3em] text-slate-500 mb-3">Recovery</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tighter mb-2">
            New Password.
          </h1>
          <p className="text-slate-500 mb-10">Enter your new password below.</p>

          <form onSubmit={handleResetPassword} className="space-y-5">
            <label className="block">
              <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">New Password</span>
              <div className="mt-2 flex items-center gap-3 border border-slate-300 dark:border-slate-700 rounded-sm px-4 py-3 focus-within:border-slate-900 dark:focus-within:border-white transition-colors">
                <Lock className="w-4 h-4 text-slate-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-transparent outline-none text-lg"
                  required
                />
              </div>
              {password && (
                <div className="mt-2 text-xs">
                  <span className={password.length >= 8 ? "text-green-600" : "text-slate-500"}>
                    {password.length >= 8 ? "✓" : "•"} At least 8 characters
                  </span>
                </div>
              )}
            </label>

            <label className="block">
              <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Confirm Password</span>
              <div className="mt-2 flex items-center gap-3 border border-slate-300 dark:border-slate-700 rounded-sm px-4 py-3 focus-within:border-slate-900 dark:focus-within:border-white transition-colors">
                <Lock className="w-4 h-4 text-slate-500" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-transparent outline-none text-lg"
                  required
                />
              </div>
              {confirmPassword && (
                <div className="mt-2 text-xs">
                  <span className={password === confirmPassword ? "text-green-600" : "text-red-600"}>
                    {password === confirmPassword ? "✓" : "✗"} Passwords match
                  </span>
                </div>
              )}
            </label>

            <button
              type="submit"
              disabled={loading || password.length < 8 || password !== confirmPassword}
              className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-4 rounded-sm font-semibold hover:bg-[#002FA7] hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? "Resetting..." : "Reset Password"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </motion.div>
      </div>
    </div>
    </>
  );
}